import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../../../components/layouts/AdminLayout';
import { getSubcategory, createSubcategory, updateSubcategory } from '../../../services/subcategoryService';
import { getCategories } from '../../../services/categoryService';
import { uploadImage } from '../../../services/uploadService';
import { toast } from 'react-toastify';
import { FiSave, FiX, FiUpload, FiChevronDown, FiChevronUp, FiTrash2, FiCheck, FiImage } from 'react-icons/fi';
import Papa from 'papaparse';

const SubcategoriesFormPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditMode = !!id;

    const [formData, setFormData] = useState({
        name: '',
        description: '',
        category: '',
        imageUrl: '',
        isActive: true,
        order: 0
    });

    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [uploadingImage, setUploadingImage] = useState(false);

    // Bulk upload states
    const [showBulkUpload, setShowBulkUpload] = useState(false);
    const [bulkSubcategories, setBulkSubcategories] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [submittingIndex, setSubmittingIndex] = useState(null);
    const [isSubmittingAll, setIsSubmittingAll] = useState(false);

    useEffect(() => {
        fetchCategories();
        if (isEditMode) {
            fetchSubcategory();
        }
    }, [id]);

    const fetchCategories = async () => {
        try {
            const response = await getCategories();
            setCategories(response.data || []);
        } catch (error) {
            console.error('Error fetching categories:', error);
            toast.error('Failed to load categories');
        }
    };

    const fetchSubcategory = async () => {
        try {
            setLoading(true);
            const response = await getSubcategory(id);
            const subcategory = response.data;

            setFormData({
                name: subcategory.name || '',
                description: subcategory.description || '',
                category: subcategory.category?._id || subcategory.category || '',
                imageUrl: subcategory.imageUrl || '',
                isActive: subcategory.isActive !== undefined ? subcategory.isActive : true,
                order: subcategory.order || 0
            });
        } catch (error) {
            console.error('Error fetching subcategory:', error);
            toast.error('Failed to load subcategory data');
            navigate('/admin/subcategories');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validate file type
            if (!file.type.startsWith('image/')) {
                toast.error('Please select an image file');
                return;
            }

            // Validate file size (5MB limit)
            if (file.size > 5 * 1024 * 1024) {
                toast.error('Image size must be less than 5MB');
                return;
            }

            setImageFile(file);
            
            // Create preview
            const reader = new FileReader();
            reader.onload = (e) => {
                setImagePreview(e.target.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleImageUpload = async () => {
        if (!imageFile) {
            toast.error('Please select an image first');
            return;
        }

        try {
            setUploadingImage(true);
            const token = localStorage.getItem('token');
            const response = await uploadImage(imageFile, token);
            
            setFormData(prev => ({
                ...prev,
                imageUrl: response.data.imageUrl
            }));
            
            toast.success('Image uploaded successfully');
        } catch (error) {
            console.error('Error uploading image:', error);
            toast.error('Failed to upload image');
        } finally {
            setUploadingImage(false);
        }
    };

    const removeImage = () => {
        setImageFile(null);
        setImagePreview(null);
        setFormData(prev => ({
            ...prev,
            imageUrl: ''
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.name.trim()) {
            toast.error('Subcategory name is required');
            return;
        }

        if (!formData.category) {
            toast.error('Please select a category');
            return;
        }

        try {
            setSubmitting(true);

            if (isEditMode) {
                await updateSubcategory(id, formData);
                toast.success(`Subcategory "${formData.name}" updated successfully`);
            } else {
                await createSubcategory(formData);
                toast.success(`Subcategory "${formData.name}" created successfully`);
            }

            navigate('/admin/subcategories');
        } catch (error) {
            console.error('Error saving subcategory:', error);
            toast.error(error.response?.data?.message || 'Failed to save subcategory');
        } finally {
            setSubmitting(false);
        }
    };

    // Bulk upload functions
    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setIsUploading(true);

        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            complete: (results) => {
                // Map the parsed data to our format
                const parsedSubcategories = results.data.map((item, index) => {
                    // Try to find category ID based on name
                    let categoryId = '';
                    const categoryName = item.category || '';

                    if (categoryName) {
                        const matchedCategory = categories.find(c =>
                            c.name.toLowerCase() === categoryName.toLowerCase()
                        );

                        if (matchedCategory) {
                            categoryId = matchedCategory._id;
                        }
                    }

                    return {
                        id: `temp-${index}`,
                        name: item.name || '',
                        description: item.description || '',
                        category: categoryId,
                        categoryName: categoryId ? '' : categoryName,
                        imageUrl: item.imageUrl || '',
                        order: parseInt(item.order) || 0,
                        isActive: item.isActive ? item.isActive.toLowerCase() === 'true' : true,
                        status: 'pending'
                    };
                });
                setBulkSubcategories(parsedSubcategories);
                setIsUploading(false);
                toast.success(`Successfully parsed ${parsedSubcategories.length} subcategories`);
            },
            error: (error) => {
                console.error('CSV Parse Error:', error);
                toast.error('Error parsing CSV file');
                setIsUploading(false);
            }
        });

        // Reset the file input
        e.target.value = null;
    };

    const handleBulkInputChange = (index, field, value) => {
        const updatedSubcategories = [...bulkSubcategories];
        if (field === 'isActive') {
            updatedSubcategories[index][field] = value;
        } else if (field === 'order') {
            updatedSubcategories[index][field] = parseInt(value) || 0;
        } else {
            updatedSubcategories[index][field] = value;
        }
        setBulkSubcategories(updatedSubcategories);
    };

    const handleRemoveRow = (index) => {
        const updatedSubcategories = [...bulkSubcategories];
        updatedSubcategories.splice(index, 1);
        setBulkSubcategories(updatedSubcategories);
    };

    const handleAddEmptyRow = () => {
        setBulkSubcategories([
            ...bulkSubcategories,
            {
                id: `temp-${Date.now()}`,
                name: '',
                description: '',
                category: '',
                imageUrl: '',
                order: 0,
                isActive: true,
                status: 'pending'
            }
        ]);
    };

    const validateBulkSubcategory = (subcategory) => {
        if (!subcategory.name.trim()) {
            return { valid: false, message: 'Name is required' };
        }
        if (!subcategory.description.trim()) {
            return { valid: false, message: 'Description is required' };
        }
        if (!subcategory.category) {
            return { valid: false, message: 'Category is required' };
        }
        // Check if category exists
        const categoryExists = categories.some(c => c._id === subcategory.category);
        if (!categoryExists) {
            return { valid: false, message: 'Selected category doesn\'t exist' };
        }
        return { valid: true };
    };

    const handleSubmitRow = async (index) => {
        const subcategory = bulkSubcategories[index];
        const validation = validateBulkSubcategory(subcategory);

        if (!validation.valid) {
            toast.error(`Row ${index + 1}: ${validation.message}`);
            return;
        }

        setSubmittingIndex(index);

        try {
            const updatedSubcategories = [...bulkSubcategories];
            updatedSubcategories[index].status = 'submitting';
            setBulkSubcategories(updatedSubcategories);

            let imageUrl = '';
            
            // Upload image if provided
            if (subcategory.imageFile) {
                const token = localStorage.getItem('token');
                const uploadResponse = await uploadImage(subcategory.imageFile, token);
                imageUrl = uploadResponse.data.imageUrl;
            }

            await createSubcategory({
                name: subcategory.name,
                description: subcategory.description,
                category: subcategory.category,
                imageUrl: imageUrl,
                order: subcategory.order,
                isActive: subcategory.isActive
            });

            updatedSubcategories[index].status = 'success';
            setBulkSubcategories(updatedSubcategories);
            toast.success(`Subcategory "${subcategory.name}" created successfully`);
        } catch (error) {
            console.error('Error creating subcategory:', error);
            const updatedSubcategories = [...bulkSubcategories];
            updatedSubcategories[index].status = 'error';
            updatedSubcategories[index].errorMessage = error.response?.data?.message || 'Failed to create subcategory';
            setBulkSubcategories(updatedSubcategories);
            toast.error(`Failed to create subcategory "${subcategory.name}": ${updatedSubcategories[index].errorMessage}`);
        } finally {
            setSubmittingIndex(null);
        }
    };

    const handleSubmitAll = async () => {
        if (bulkSubcategories.length === 0) {
            toast.warning('No subcategories to submit');
            return;
        }

        // Validate all subcategories first
        for (let i = 0; i < bulkSubcategories.length; i++) {
            const subcategory = bulkSubcategories[i];
            if (subcategory.status === 'success') continue; // Skip already successful ones

            const validation = validateBulkSubcategory(subcategory);
            if (!validation.valid) {
                toast.error(`Row ${i + 1}: ${validation.message}`);
                return;
            }
        }

        setIsSubmittingAll(true);

        try {
            const updatedSubcategories = [...bulkSubcategories];
            let successCount = 0;
            let errorCount = 0;

            for (let i = 0; i < updatedSubcategories.length; i++) {
                const subcategory = updatedSubcategories[i];

                // Skip already processed subcategories
                if (subcategory.status === 'success') {
                    successCount++;
                    continue;
                }

                try {
                    setSubmittingIndex(i);

                    // Update status to submitting
                    updatedSubcategories[i].status = 'submitting';
                    setBulkSubcategories([...updatedSubcategories]);

                    let imageUrl = '';
                    
                    // Upload image if provided
                    if (subcategory.imageFile) {
                        const token = localStorage.getItem('token');
                        const uploadResponse = await uploadImage(subcategory.imageFile, token);
                        imageUrl = uploadResponse.data.imageUrl;
                    }

                    await createSubcategory({
                        name: subcategory.name,
                        description: subcategory.description,
                        category: subcategory.category,
                        imageUrl: imageUrl,
                        order: subcategory.order,
                        isActive: subcategory.isActive
                    });

                    updatedSubcategories[i].status = 'success';
                    successCount++;
                } catch (error) {
                    console.error('Error creating subcategory:', error);
                    updatedSubcategories[i].status = 'error';
                    updatedSubcategories[i].errorMessage = error.response?.data?.message || 'Failed to create subcategory';
                    errorCount++;
                }

                // Update the state after each operation
                setBulkSubcategories([...updatedSubcategories]);
            }

            if (successCount > 0) {
                toast.success(`Successfully created ${successCount} subcategories`);
            }
            if (errorCount > 0) {
                toast.error(`Failed to create ${errorCount} subcategories`);
            }
        } finally {
            setIsSubmittingAll(false);
            setSubmittingIndex(null);
        }
    };

    const downloadSampleCSV = () => {
        const headers = ['name', 'description', 'category', 'order', 'isActive'];

        // Get real category names for the sample if available
        const categoryName1 = categories.length > 0 ? categories[0].name : 'Skincare';
        const categoryName2 = categories.length > 1 ? categories[1].name : 'Hair Care';

        const sampleData = [
            {
                name: 'Cleansers',
                description: 'Face cleansing products',
                category: categoryName1, // Using category name instead of ID
                order: 1,
                isActive: 'true'
            },
            {
                name: 'Moisturizers',
                description: 'Face moisturizing products',
                category: categoryName2, // Using category name instead of ID
                order: 2,
                isActive: 'true'
            }
        ];

        const csvContent = Papa.unparse({
            fields: headers,
            data: sampleData
        });

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'subcategory_template.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const clearBulkData = () => {
        if (window.confirm('Are you sure you want to clear all bulk data?')) {
            setBulkSubcategories([]);
        }
    };

    return (
        <AdminLayout>
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-800">
                    {isEditMode ? 'Edit Subcategory' : 'Create Subcategory'}
                </h1>

                {!isEditMode && (
                    <button
                        type="button"
                        className="btn btn-outline flex items-center"
                        onClick={() => setShowBulkUpload(!showBulkUpload)}
                    >
                        {showBulkUpload ? <FiChevronUp className="mr-2" /> : <FiChevronDown className="mr-2" />}
                        {showBulkUpload ? 'Hide Bulk Upload' : 'Show Bulk Upload'}
                    </button>
                )}
            </div>

            {loading ? (
                <div className="flex justify-center py-10">
                    <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary-600"></div>
                </div>
            ) : (
                <>
                    {/* Single Subcategory Form */}
                    {(!showBulkUpload || isEditMode) && (
                        <div className="bg-[#f8f8f8] shadow-md rounded-lg overflow-hidden mb-6">
                            <form onSubmit={handleSubmit} className="p-6">
                                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                                    <div className="col-span-2">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Category*
                                        </label>
                                        <select
                                            name="category"
                                            value={formData.category}
                                            onChange={handleChange}
                                            className="input"
                                            required
                                        >
                                            <option value="">Select a Category</option>
                                            {categories.map(category => (
                                                <option key={category._id} value={category._id}>
                                                    {category.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="col-span-2">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Subcategory Name*
                                        </label>
                                        <input
                                            type="text"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            className="input"
                                            required
                                        />
                                    </div>

                                    <div className="col-span-2">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Description*
                                        </label>
                                        <textarea
                                            name="description"
                                            value={formData.description}
                                            onChange={handleChange}
                                            className="input min-h-[100px]"
                                            required
                                        ></textarea>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Subcategory Image
                                        </label>
                                        
                                        {/* Image Upload Section */}
                                        <div className="space-y-3">
                                            {/* File Input */}
                                            <div className="flex items-center space-x-3">
                                                <label className="flex items-center px-4 py-2 bg-gray-100 border border-gray-300 rounded-md cursor-pointer hover:bg-gray-200 transition-colors">
                                                    <FiImage className="w-4 h-4 mr-2" />
                                                    Choose Image
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        onChange={handleImageChange}
                                                        className="hidden"
                                                    />
                                                </label>
                                                
                                                {imageFile && (
                                                    <button
                                                        type="button"
                                                        onClick={handleImageUpload}
                                                        disabled={uploadingImage}
                                                        className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                                    >
                                                        <FiUpload className="w-4 h-4 mr-2" />
                                                        {uploadingImage ? 'Uploading...' : 'Upload'}
                                                    </button>
                                                )}
                                                
                                                {(imageFile || formData.imageUrl) && (
                                                    <button
                                                        type="button"
                                                        onClick={removeImage}
                                                        className="flex items-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                                                    >
                                                        <FiX className="w-4 h-4 mr-2" />
                                                        Remove
                                                    </button>
                                                )}
                                            </div>
                                            
                                            {/* Image Preview */}
                                            {(imagePreview || formData.imageUrl) && (
                                                <div className="mt-3">
                                                    <img
                                                        src={imagePreview || formData.imageUrl}
                                                        alt="Preview"
                                                        className="h-32 w-32 object-cover rounded-md border border-gray-200"
                                                    />
                                                </div>
                                            )}
                                            
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Display Order
                                        </label>
                                        <input
                                            type="number"
                                            name="order"
                                            value={formData.order}
                                            onChange={handleChange}
                                            className="input"
                                            min="0"
                                        />
                                    </div>

                                    <div className="col-span-2">
                                        <div className="flex items-center">
                                            <input
                                                type="checkbox"
                                                name="isActive"
                                                checked={formData.isActive}
                                                onChange={handleChange}
                                                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                            />
                                            <label className="ml-2 block text-sm text-gray-700">
                                                Active
                                            </label>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-6 flex items-center justify-end space-x-3">
                                    <button
                                        type="button"
                                        onClick={() => navigate('/admin/subcategories')}
                                        className="btn btn-outline flex items-center"
                                    >
                                        <FiX className="mr-2" /> Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        className={`btn btn-primary flex items-center ${submitting ? 'opacity-70 cursor-not-allowed' : ''}`}
                                    >
                                        {submitting ? (
                                            <>
                                                <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                                                Saving...
                                            </>
                                        ) : (
                                            <>
                                                <FiSave className="mr-2" /> Save
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}

                    {/* Bulk Upload Section */}
                    {showBulkUpload && !isEditMode && (
                        <div className="bg-[#f8f8f8] shadow-md rounded-lg overflow-hidden mb-6">
                            <div className="p-6">
                                <h2 className="text-xl font-semibold mb-4">Bulk Create Subcategories</h2>
                                <div className="mb-6">
                                    <p className="text-sm text-gray-600 mb-3">
                                        Upload a CSV file with subcategory data or add entries manually. The CSV should include columns for name, description, category, order, and isActive. Images can be uploaded individually for each subcategory.
                                    </p>
                                    <div className="flex flex-wrap items-center gap-4">
                                        <label className="btn btn-primary flex items-center cursor-pointer">
                                            <FiUpload className="mr-2" />
                                            Upload CSV
                                            <input
                                                type="file"
                                                accept=".csv"
                                                onChange={handleFileUpload}
                                                className="hidden"
                                                disabled={isUploading}
                                            />
                                        </label>

                                        <button
                                            onClick={downloadSampleCSV}
                                            className="btn btn-outline flex items-center text-sm"
                                        >
                                            <FiUpload className="mr-1" /> Download Template
                                        </button>

                                        <button
                                            onClick={handleAddEmptyRow}
                                            className="btn btn-outline flex items-center"
                                        >
                                            <FiUpload className="mr-2" /> Add Row
                                        </button>

                                        {bulkSubcategories.length > 0 && (
                                            <>
                                                <button
                                                    onClick={clearBulkData}
                                                    className="btn btn-outline flex items-center text-red-600 border-red-600 hover:bg-red-50"
                                                >
                                                    <FiTrash2 className="mr-2" /> Clear All
                                                </button>

                                                <button
                                                    onClick={handleSubmitAll}
                                                    disabled={isSubmittingAll || bulkSubcategories.length === 0}
                                                    className={`btn btn-primary flex items-center ml-auto ${isSubmittingAll ? 'opacity-70 cursor-not-allowed' : ''}`}
                                                >
                                                    {isSubmittingAll ? (
                                                        <>
                                                            <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                                                            Saving All...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <FiSave className="mr-2" /> Save All
                                                        </>
                                                    )}
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>

                                {isUploading ? (
                                    <div className="text-center py-10">
                                        <div className="inline-block animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary-600 mb-4"></div>
                                        <p>Parsing CSV...</p>
                                    </div>
                                ) : bulkSubcategories.length > 0 ? (
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Name*
                                                    </th>
                                                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Description*
                                                    </th>
                                                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Category*
                                                    </th>
                                                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Image
                                                    </th>
                                                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Order
                                                    </th>
                                                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Active
                                                    </th>
                                                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Status
                                                    </th>
                                                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Actions
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-[#f8f8f8] divide-y divide-gray-200">
                                                {bulkSubcategories.map((subcategory, index) => (
                                                    <tr key={subcategory.id} className={`${subcategory.status === 'error' ? 'bg-red-50' : subcategory.status === 'success' ? 'bg-green-50' : ''}`}>
                                                        <td className="px-3 py-2">
                                                            <input
                                                                type="text"
                                                                value={subcategory.name}
                                                                onChange={(e) => handleBulkInputChange(index, 'name', e.target.value)}
                                                                disabled={subcategory.status === 'success' || subcategory.status === 'submitting' || submittingIndex === index}
                                                                className="input py-1 px-2 text-sm"
                                                                placeholder="Subcategory name"
                                                                required
                                                            />
                                                        </td>
                                                        <td className="px-3 py-2">
                                                            <input
                                                                type="text"
                                                                value={subcategory.description}
                                                                onChange={(e) => handleBulkInputChange(index, 'description', e.target.value)}
                                                                disabled={subcategory.status === 'success' || subcategory.status === 'submitting' || submittingIndex === index}
                                                                className="input py-1 px-2 text-sm"
                                                                placeholder="Description"
                                                                required
                                                            />
                                                        </td>
                                                        <td className="px-3 py-2">
                                                            <select
                                                                value={subcategory.category}
                                                                onChange={(e) => handleBulkInputChange(index, 'category', e.target.value)}
                                                                disabled={subcategory.status === 'success' || subcategory.status === 'submitting' || submittingIndex === index}
                                                                className={`input py-1 px-2 text-sm ${!subcategory.category && subcategory.categoryName ? 'border-yellow-400' : ''}`}
                                                                required
                                                            >
                                                                <option value="">Select Category</option>
                                                                {categories.map(category => (
                                                                    <option key={category._id} value={category._id}>
                                                                        {category.name}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                            {!subcategory.category && subcategory.categoryName && (
                                                                <p className="mt-1 text-xs text-yellow-600">
                                                                    Could not find "{subcategory.categoryName}"
                                                                </p>
                                                            )}
                                                        </td>
                                                        <td className="px-3 py-2">
                                                            <div className="flex items-center space-x-2">
                                                                <label className="flex items-center px-2 py-1 bg-gray-100 border border-gray-300 rounded text-xs cursor-pointer hover:bg-gray-200 transition-colors">
                                                                    <FiImage className="w-3 h-3 mr-1" />
                                                                    Upload
                                                                    <input
                                                                        type="file"
                                                                        accept="image/*"
                                                                        onChange={(e) => {
                                                                            const file = e.target.files[0];
                                                                            if (file) {
                                                                                // Validate file type
                                                                                if (!file.type.startsWith('image/')) {
                                                                                    toast.error('Please select an image file');
                                                                                    return;
                                                                                }
                                                                                // Validate file size (5MB limit)
                                                                                if (file.size > 5 * 1024 * 1024) {
                                                                                    toast.error('Image size must be less than 5MB');
                                                                                    return;
                                                                                }
                                                                                // Store file for upload
                                                                                const updatedSubcategories = [...bulkSubcategories];
                                                                                updatedSubcategories[index].imageFile = file;
                                                                                setBulkSubcategories(updatedSubcategories);
                                                                            }
                                                                        }}
                                                                        className="hidden"
                                                                        disabled={subcategory.status === 'success' || subcategory.status === 'submitting' || submittingIndex === index}
                                                                    />
                                                                </label>
                                                                {subcategory.imageFile && (
                                                                    <span className="text-xs text-green-600">
                                                                        {subcategory.imageFile.name}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td className="px-3 py-2">
                                                            <input
                                                                type="number"
                                                                value={subcategory.order}
                                                                onChange={(e) => handleBulkInputChange(index, 'order', e.target.value)}
                                                                disabled={subcategory.status === 'success' || subcategory.status === 'submitting' || submittingIndex === index}
                                                                className="input py-1 px-2 text-sm w-16"
                                                                min="0"
                                                            />
                                                        </td>
                                                        <td className="px-3 py-2">
                                                            <input
                                                                type="checkbox"
                                                                checked={subcategory.isActive}
                                                                onChange={(e) => handleBulkInputChange(index, 'isActive', e.target.checked)}
                                                                disabled={subcategory.status === 'success' || subcategory.status === 'submitting' || submittingIndex === index}
                                                                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                                            />
                                                        </td>
                                                        <td className="px-3 py-2">
                                                            {subcategory.status === 'submitting' || submittingIndex === index ? (
                                                                <span className="inline-flex items-center px-2 py-1 text-xs font-medium">
                                                                    <div className="animate-spin rounded-full h-3 w-3 border-t-2 border-b-2 border-primary-600 mr-1"></div>
                                                                    Submitting...
                                                                </span>
                                                            ) : subcategory.status === 'success' ? (
                                                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                                    <FiCheck className="mr-1" />
                                                                    Success
                                                                </span>
                                                            ) : subcategory.status === 'error' ? (
                                                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800" title={subcategory.errorMessage}>
                                                                    <FiX className="mr-1" />
                                                                    Error
                                                                </span>
                                                            ) : (
                                                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                                                    Pending
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td className="px-3 py-2">
                                                            <div className="flex space-x-2">
                                                                {subcategory.status !== 'success' && (
                                                                    <>
                                                                        <button
                                                                            onClick={() => handleSubmitRow(index)}
                                                                            disabled={subcategory.status === 'submitting' || isSubmittingAll || submittingIndex === index}
                                                                            className="text-primary-600 hover:text-primary-900 p-1"
                                                                            title="Save this subcategory"
                                                                        >
                                                                            <FiSave size={16} />
                                                                        </button>
                                                                        <button
                                                                            onClick={() => handleRemoveRow(index)}
                                                                            disabled={subcategory.status === 'submitting' || isSubmittingAll || submittingIndex === index}
                                                                            className="text-red-600 hover:text-red-900 p-1"
                                                                            title="Remove this row"
                                                                        >
                                                                            <FiTrash2 size={16} />
                                                                        </button>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <div className="text-center py-8 text-gray-500">
                                        <p>No subcategories added yet. Upload a CSV or add rows manually.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </>
            )}
        </AdminLayout>
    );
};

export default SubcategoriesFormPage;